import {
  GetQuestionReviewTaskByIdDocument,
  SubmitQuestionReviewErrorCode,
  useSubmitQuestionReviewMutation,
} from '@exam-management/graphql';
import { useAuth } from '@shared/auth';
import { useDisclosure } from '@shared/hooks';
import { useSnackbar } from '@shared/snackbar';
import { useState } from 'react';

import { QuestionReviewFinishTaskConfirmationModal } from '../../components';
import { useQuestionReviewTaskDetails } from '../../providers';
import { SubmitReviewErrorModal } from '../submit-review-error-modal';

interface FinishQuestionReviewSubtaskModalProps {
  open: boolean;
  onClose: () => void;
  onOverdue: () => void;
}

export default function FinishQuestionReviewSubtaskModal(
  props: Readonly<FinishQuestionReviewSubtaskModalProps>,
) {
  const { open: isOpen, onClose, onOverdue } = props;
  const { taskDetails, questions } = useQuestionReviewTaskDetails();
  const { userProfile } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [missingQuestionVerdictCount, setMissingQuestionVerdictCount] = useState(0);
  const [isErrorModalOpen, { open: openErrorModal, close: closeErrorModal }] = useDisclosure();
  const taskId = taskDetails?.id;

  const [submitQuestionReview, { loading: isLoading }] = useSubmitQuestionReviewMutation({
    onCompleted: (data) => {
      if (!data.wflSubmitQuestionReview.success) {
        const { code, missingQuestionVerdictCount: count } = data.wflSubmitQuestionReview;
        setMissingQuestionVerdictCount(count);
        if (code === null) {
          showSnackbar({
            message: 'Failed to submit review',
            variant: 'error',
          });
          return;
        }
        if (code === SubmitQuestionReviewErrorCode.TaskOverdue) {
          onClose();
          onOverdue();
          return;
        }
        openErrorModal();
      } else {
        showSnackbar({
          message: 'Task has been finished',
          variant: 'success',
        });
      }
    },
    onError: () => {
      showSnackbar({
        message: 'Unable to finish task',
        variant: 'error',
      });
    },
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetQuestionReviewTaskByIdDocument,
        variables: {
          input: {
            taskId: taskDetails.id,
          },
        },
      },
    ],
  });

  const handleFinishTask = async () => {
    await submitQuestionReview({
      variables: {
        submitQuestionReviewInput: {
          taskId,
        },
      },
    });

    onClose();
  };

  const ownedVerdictQuestions = questions.filter((question) => {
    return question.verdicts.find((taskVerdict) => taskVerdict.userId === userProfile?.id);
  });
  const incompleteQuestions = questions.length - ownedVerdictQuestions.length;

  if (incompleteQuestions === 0) {
    return (
      <QuestionReviewFinishTaskConfirmationModal
        open={isOpen}
        loading={isLoading}
        onClose={onClose}
        onAccept={handleFinishTask}
      />
    );
  }

  if (isErrorModalOpen) {
    return (
      <SubmitReviewErrorModal
        open={isErrorModalOpen}
        missingQuestionVerdictCount={missingQuestionVerdictCount}
        onClose={closeErrorModal}
      />
    );
  }

  return (
    <SubmitReviewErrorModal
      open={isOpen}
      onClose={onClose}
      missingQuestionVerdictCount={incompleteQuestions}
    />
  );
}
